import { useState } from 'react'
import type { PlanConfig } from '../../../types/plan.types'
import { ElitePlansEventId, trackElitePlansClick } from '../../../analytics'
import { ChevronRightIcon } from '../../../icons/ChevronRightIcon'
import VaulDrawer from '../../../../../components/BottomSheetComponents/VaulDrawer'
import { postCTA as postCta } from '../../../../../utils/appBridge'
import { getStaticSiteCta } from '../../../../../utils/commonAppCTAs'
import {
  ElitePlansPricingDetailsBottomSheet,
  elitePlansPricingDetailsTitleId
} from '../../ElitePlansPricingDetailsBottomSheet'
import { ElitePlansPrimeLearnCarousel } from '../../ElitePlansPrimeLearnCarousel'
import { LowestPriceAssurance } from './LowestPriceAssurance'
import { ElitePlansPlusBannerRow } from './ElitePlansPlusBannerRow'

export function ElitePlansPlusBanner({
  planId,
  plusBanner
}: {
  planId: string
  plusBanner: NonNullable<PlanConfig['plusBanner']>
}) {
  const [isPricingDetailsOpen, setIsPricingDetailsOpen] = useState(false)
  const detailsLabel = plusBanner.detailsLabel ?? ''
  const philosophyLabel = plusBanner.philosophyLabel ?? ''
  const pricingDetails = plusBanner.pricingDetails

  const openPricingDetails = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId,
      buttonLabel: detailsLabel || 'Pricing details'
    })
    if (pricingDetails) {
      setIsPricingDetailsOpen(true)
    }
  }

  const handlePhilosophy = () => {
    trackElitePlansClick(ElitePlansEventId.ViewDetails, {
      planId,
      buttonLabel: philosophyLabel || 'Pricing philosophy'
    })
    postCta(JSON.stringify(getStaticSiteCta('ELITE_LANDING_PRICING')))
  }

  return (
    <section className='mt-6 flex w-full flex-col items-center gap-1 text-left'>
      <LowestPriceAssurance title={plusBanner.title} />

      <button
        type='button'
        className='ep-focus-ring mt-4 w-full overflow-hidden rounded-2xl border-none bg-[color:var(--ep-plus-card)] p-0 text-left'
        onClick={openPricingDetails}
        aria-label={detailsLabel || 'View pricing details'}
      >
        <ul className='m-0 list-none divide-y divide-[color:var(--ep-plus-divider)] p-0'>
          {plusBanner.items.map((item) => (
            <li key={item.id}>
              <ElitePlansPlusBannerRow item={item} />
            </li>
          ))}
        </ul>

        {detailsLabel ? (
          <div className='flex min-h-12 w-full items-center justify-between gap-3 border-t border-solid border-[color:var(--ep-plus-divider)] px-4 py-3.5 text-[13px] font-normal text-[color:var(--ep-plus-link)]'>
            <span>{detailsLabel}</span>
            <ChevronRightIcon size={10} color='var(--ep-plus-link)' className='shrink-0 opacity-70' />
          </div>
        ) : null}
      </button>

      {philosophyLabel ? (
        <button
          type='button'
          onClick={handlePhilosophy}
          className='ep-focus-ring inline-flex min-h-11 items-center gap-1 border-none bg-transparent px-3.5 py-0'
          aria-label={philosophyLabel}
        >
          <span className='text-[13px] font-medium leading-[1.3] text-prime70'>
            {philosophyLabel}
          </span>
          <ChevronRightIcon size={10} color='#732e17' className='shrink-0' />
        </button>
      ) : null}

      <ElitePlansPrimeLearnCarousel planId={planId} />

      {pricingDetails ? (
        <VaulDrawer
          open={isPricingDetailsOpen}
          setOpen={setIsPricingDetailsOpen}
          titleId={elitePlansPricingDetailsTitleId}
          className='!bg-black'
          focusDrawerOnOpen
        >
          <ElitePlansPricingDetailsBottomSheet
            open={isPricingDetailsOpen}
            template={pricingDetails}
            onClose={() => setIsPricingDetailsOpen(false)}
          />
        </VaulDrawer>
      ) : null}
    </section>
  )
}
